import { ORDERING_SUBDOMAIN_PROD } from 'constants/chownow';
import { URL_PARAMS } from 'constants/urlFiltering';

export function getLastPath(path: string) {
  const noQueryParamPath = path.split('?')[0];

  if (noQueryParamPath.lastIndexOf('/') === -1) {
    return '';
  }

  return noQueryParamPath.substring(noQueryParamPath.lastIndexOf('/') + 1);
}

export function getAppOrderDirectDomain() {
  let domain;

  if (import.meta.env.VITE_DEPLOY_ENV === 'local') {
    domain = 'web.dev.chownow.com:3000/order';
  } else if (import.meta.env.VITE_DEPLOY_ENV === 'prod') {
    domain = `${ORDERING_SUBDOMAIN_PROD}.chownow.com/order`;
  } else {
    domain = `app-order-direct.${import.meta.env.VITE_DEPLOY_ENV}.svpn.chownow.com/order`;
  }

  return `https://${domain}`;
}

export function getUrlParam(param: string) {
  const currentURL = window.location.href;
  const regex = new RegExp(`\\/${param}\\/(\\d+)`);
  const urlParam = regex.exec(currentURL);

  return urlParam && urlParam[1];
}

export function getSearchParam(param: string) {
  const queryString = window.location.search;
  const searchParams = new URLSearchParams(queryString);
  const searchParam = searchParams.get(`${param}`);

  return searchParam && searchParam;
}

// Specifically for locations path, create path pattern so we can replace
// restaurant ID when using the locations selector
// example: '/restaurant/:restaurantId/promos'
export function getPathPattern({
  pathname,
  restaurantId,
}: {
  pathname: string;
  restaurantId: string;
}): string {
  const pathArray = pathname.split('/');
  const restoIdIndex = pathArray.findIndex(element => element === restaurantId);
  pathArray[restoIdIndex] = ':restaurantId';

  return pathArray.join('/');
}

/**
 * Sanitizes search input by removing potentially dangerous characters
 * while preserving alphanumeric characters, spaces, and common punctuation.
 * This prevents injection attacks and encoding issues.
 * @param value - The search string to sanitize
 * @returns Sanitized search string safe for URL parameters
 */
export function sanitizeSearchQuery(value: string): string {
  if (!value) return '';

  return value
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/[^a-zA-Z0-9\s.,\-()&'+~!"]/g, '')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, 100);
}

interface Filters {
  page?: string | number | null;
  page_size?: string | number | null;
  query?: string | null;
}

export function buildFilterQuery(filters: Filters): string {
  const { page, page_size: pageSize, query } = filters;
  const queryParams = new URLSearchParams({});

  if (page) {
    queryParams.set(URL_PARAMS.PAGE, `${page}`);
  }
  if (pageSize) {
    queryParams.set(URL_PARAMS.PAGE_SIZE, `${pageSize}`);
  }
  if (query) {
    // Sanitize query to remove dangerous characters
    const sanitizedQuery = sanitizeSearchQuery(query);
    if (sanitizedQuery) {
      // URLSearchParams.set() automatically handles URL encoding (e.g., spaces become %20)
      queryParams.set(URL_PARAMS.SEARCH, sanitizedQuery);
    }
  }

  const strQueryParams = queryParams.toString();
  return strQueryParams.length > 0 ? `?${strQueryParams}` : '';
}

export function isAbsoluteUrl(url: string) {
  try {
    // eslint-disable-next-line no-new
    new URL(url);
    return true;
  } catch {
    return false;
  }
}

export function isAllowedNextUrl(next: string) {
  try {
    const url = new URL(next);
    return url.hostname.endsWith('.chownow.com') && url.protocol === 'https:';
  } catch {
    return next.startsWith('/');
  }
}

/**
 * Transforms legacy company-based URLs to the new Portal format
 * @param companyId - The company ID from the URL
 * @param location - The location object containing pathname and search
 * @returns The redirect path
 */
export function getCompanyRedirectPath(
  companyId: string,
  location: { pathname: string; search: string },
): string {
  // Extract the path after /company/:companyId
  const pathParts = location.pathname.split('/');
  const companyIndex = pathParts.indexOf('company');

  if (companyIndex >= 0 && pathParts[companyIndex + 2] === 'restaurant') {
    // Transform to Portal's format: /restaurant/:restaurantId/orders/:orderId
    const restaurantId = pathParts[companyIndex + 3];
    const ordersIndex = pathParts.indexOf('orders', companyIndex);
    if (
      ordersIndex >= 0 &&
      pathParts[ordersIndex + 1] === 'details' &&
      pathParts[ordersIndex + 2]
    ) {
      const orderId = pathParts[ordersIndex + 2];
      const redirectTo = `/restaurant/${restaurantId}/orders/${orderId}${location.search}`;
      return redirectTo;
    }
    // Fallback: strip company prefix and redirect to /restaurant/:restaurantId/...
    const remainingPath = pathParts.slice(companyIndex + 2).join('/');
    return remainingPath ? `/${remainingPath}${location.search}` : `/company/${companyId}`;
  }
  // Default: redirect to company page
  return `/company/${companyId}`;
}
